// Shared logic for walking the dynamic, Typeform-derived project-scope questionnaire
// (see backend/services/typeformSchema.js for how the schema this consumes is built).
// Used by both FoundingForm (the standalone founding-offer flow) and ContactSection
// (which appends the same questions after its own plain Name/Email/Company fields).

// condition can be a leaf ('is'/'is_not' against one field) or a compound ('and'/'or'
// of other conditions) - Typeform uses compound conditions for every industry-gated
// branch (e.g. "industry is Healthcare AND building-type is not a plain brochure site"),
// so evalCondition needs the full answers map, not just the current field's value, to
// look up whichever field each leaf condition actually points at.
export function evalCondition(condition, valuesByRef) {
  if (!condition) return true;
  if (condition.op === 'always') return true;
  if (condition.op === 'and') return condition.vars.every((c) => evalCondition(c, valuesByRef));
  if (condition.op === 'or') return condition.vars.some((c) => evalCondition(c, valuesByRef));

  const fieldVar = condition.vars?.find((v) => v.type === 'field');
  const choiceVar = condition.vars?.find((v) => v.type === 'choice');
  if (!fieldVar || !choiceVar) return false;

  const value = valuesByRef[fieldVar.value];
  const selected = Array.isArray(value) ? value : [value];
  if (condition.op === 'is') return selected.includes(choiceVar.value);
  if (condition.op === 'is_not') return !selected.includes(choiceVar.value);
  return false;
}

export function resolveNextRef(currentRef, valuesByRef, schema) {
  const rule = schema.logic.find((r) => r.ref === currentRef);
  if (rule) {
    const matched = rule.actions.find((action) => evalCondition(action.condition, valuesByRef));
    // Only follow a jump if the target exists in the flat field list — Typeform logic can
    // reference group containers, thank-you screens, or deleted fields that won't appear
    // in schema.fields. Fall through to sequential in those cases.
    if (matched && schema.fields.some((f) => f.ref === matched.targetRef)) {
      return matched.targetRef;
    }
  }
  const idx = schema.fields.findIndex((f) => f.ref === currentRef);
  return schema.fields[idx + 1]?.ref ?? null;
}

// Like resolveNextRef, but keeps skipping forward past any field shouldSkip() flags -
// e.g. the contact_info fields (name/email/phone/company), already collected elsewhere.
export function resolveNextVisibleRef(currentRef, valuesByRef, schema, shouldSkip) {
  let nextRef = resolveNextRef(currentRef, valuesByRef, schema);
  while (nextRef && shouldSkip?.(schema.fields.find((f) => f.ref === nextRef))) {
    nextRef = resolveNextRef(nextRef, valuesByRef, schema);
  }
  return nextRef;
}

export function firstVisibleRef(schema, shouldSkip) {
  const first = schema.fields[0];
  if (!first) return null;
  if (!shouldSkip?.(first)) return first.ref;
  return resolveNextVisibleRef(first.ref, {}, schema, shouldSkip);
}

export function choiceLabel(field, choiceRef) {
  return field.properties?.choices?.find((c) => c.ref === choiceRef)?.label ?? choiceRef;
}

export function isAnswered(field, value) {
  if (field.type === 'statement') return true;
  if (!field.required) return true;
  if (Array.isArray(value)) return value.length > 0;
  return value !== undefined && value !== null && value.toString().trim() !== '';
}

// Returns an error string if the value is invalid, or null if valid.
// isAnswered() gates the Continue button; validateField() runs on Continue click
// and catches format errors that isAnswered() can't detect (bad email, short phone, etc.).
export function validateField(field, value) {
  if (!isAnswered(field, value)) return field.required ? 'This field is required' : null;
  const str = Array.isArray(value) ? '' : (value ?? '').toString().trim();
  if (field.type === 'email' && str) {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str)) return 'Please enter a valid email address';
  }
  if (field.type === 'phone_number' && str) {
    if (!/^\+?[\d\s\-().]{6,}$/.test(str)) return 'Please enter a valid phone number';
  }
  if (field.type === 'url' && str) {
    try { new URL(str.startsWith('http') ? str : `https://${str}`); }
    catch { return 'Please enter a valid URL'; }
  }
  return null;
}


// Converts the raw {ref: value} map into a {question title: readable answer} map
// suitable for storing in form_answers and for an admin to read later.
export function buildReadableAnswers(schema, valuesByRef) {
  const answers = {};
  for (const field of schema.fields) {
    const value = valuesByRef[field.ref];
    if (value === undefined) continue;
    if (Array.isArray(value)) {
      answers[field.title] = value.map((ref) => choiceLabel(field, ref));
    } else if (field.properties?.choices) {
      answers[field.title] = choiceLabel(field, value);
    } else {
      answers[field.title] = value;
    }
  }
  return answers;
}

export const TEXT_INPUT_TYPES = { short_text: 'text', email: 'email', phone_number: 'tel', url: 'url', number: 'number' };

// Typeform has the contact_info block (name/email/phone/company) last, which made
// sense as a Typeform but reads as "answer everything, then we ask who you are" in a
// standalone application flow. Moves those fields to the front instead. Safe because
// the contact fields never appear as a jump target or source in `logic` - reordering
// them doesn't touch any branch.
export function reorderContactFieldsFirst(schema) {
  const contactFields = schema.fields.filter((f) => f.contactInfoKey);
  const restFields = schema.fields.filter((f) => !f.contactInfoKey);
  return { ...schema, fields: [...contactFields, ...restFields] };
}
